/**
 * TutorialOverlay Component
 * Main tutorial overlay that orchestrates spotlight and tutorial card
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTutorial } from "../../hooks/useTutorial";
import { useApp } from "../../helpers/app-context";
import { Spotlight } from "./Spotlight";
import { TutorialCard } from "./TutorialCard";
import { ExitConfirmationModal } from "./ExitConfirmationModal";
import { useTutorialNarration } from "./useTutorialNarration";
import { TUTORIAL_STORAGE_KEYS } from "./types";
import { pushPromptOverlay } from "../../pwa/prompt-overlay";

export function TutorialOverlay() {
  const {
    isActive,
    getCurrentStep,
    exitTutorial,
    nextStep,
    currentStepIndex,
    getTotalSteps,
  } = useTutorial();

  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = getCurrentStep();
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const { setIsChatBotOpen, setTourChatModeRequest } = useApp() as {
    setIsChatBotOpen: (open: boolean) => void;
    setTourChatModeRequest: (mode: "chat" | "agent" | null) => void;
  };

  // Narration is always available; the backend's ElevenLabs key is the
  // effective kill switch (missing key -> 503 -> the tour continues silently).
  const [narrationEnabled, setNarrationEnabled] = useState(
    () => localStorage.getItem(TUTORIAL_STORAGE_KEYS.narrationEnabled) !== "false"
  );
  const toggleNarration = () => {
    setNarrationEnabled((prev) => {
      localStorage.setItem(TUTORIAL_STORAGE_KEYS.narrationEnabled, prev ? "false" : "true");
      return !prev;
    });
  };

  // Re-sync with localStorage whenever a tour activates — WelcomeTourCard
  // writes the narration preference right before starting the tour, but this
  // component is mounted once in App.tsx, so its initializer only ran on
  // first mount and would otherwise miss that write.
  useEffect(() => {
    if (!isActive) return;
    setNarrationEnabled(
      localStorage.getItem(TUTORIAL_STORAGE_KEYS.narrationEnabled) !== "false"
    );
  }, [isActive]);

  // Register the tour's scrim with the prompt-overlay registry while active,
  // so deferring nudges (feature announcements) stay hidden during a tour.
  useEffect(() => {
    if (!isActive) return;
    return pushPromptOverlay();
  }, [isActive]);

  // Pending auto-advance timer, so a manual nav (Previous/Skip/Next) or exit
  // during the post-narration pause can cancel it before it fires nextStep().
  const autoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tracks whether the user's last move was backward, so a replayed clip on a
  // revisited step doesn't auto-advance them forward again and undo the Back.
  const lastStepIndexRef = useRef(currentStepIndex);
  const movedBackRef = useRef(false);

  useEffect(() => {
    movedBackRef.current = currentStepIndex < lastStepIndexRef.current;
    lastStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  const { isNarrating } = useTutorialNarration({
    step: currentStep,
    isActive,
    enabled: narrationEnabled,
    onAudioEnded: () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
      // Replaying a step the user navigated back to should not yank them
      // forward again — let them re-hear it without losing their Back.
      if (!isActive || movedBackRef.current) return;
      // Small breath before auto-advancing; nextStep() completes the tour on the last step
      autoAdvanceTimeoutRef.current = setTimeout(() => nextStep(), 800);
    },
  });

  // Cancel a pending auto-advance on any step change or tour exit — a stale
  // timer must never fire nextStep() after the user has already moved on.
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, [currentStep?.id, isActive]);

  // Navigate to step's route if needed (supports query params like /marketing?tab=ads-preview)
  useEffect(() => {
    if (!isActive || !currentStep) return;

    const currentFull = location.pathname + location.search;

    if (currentStep.route !== currentFull) {
      navigate(currentStep.route);
    }
  }, [currentStep, isActive, location.pathname, location.search, navigate]);

  // Apply the step's chatbot state once on step entry; after that, the
  // user's manual open/close wins for the rest of the step. Deliberately
  // leaves the chat open when the tour ends on a chat step — an open chat
  // invites the customer's first question.
  useEffect(() => {
    if (!isActive) return;
    const desired = currentStep?.chatbotState;
    if (!desired) return;
    setIsChatBotOpen(desired === "open");
    if (currentStep?.chatMode) {
      setTourChatModeRequest(currentStep.chatMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id, isActive]);

  // Scroll target element into view after navigation settles
  useEffect(() => {
    if (!isActive || !currentStep?.scrollIntoView) return;

    const timeout = setTimeout(() => {
      try {
        const element = document.querySelector(currentStep.targetSelector);
        element?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      } catch (error) {
        console.warn("Tutorial: Could not scroll element into view:", error);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [currentStep, isActive]);

  // Auto-skip steps based on DOM conditions:
  // - skipIfTargetMissing: advance when target is NOT in the DOM (e.g., Connect step for connected users)
  // - skipIfElementExists: exit when a blocker IS in the DOM (e.g., section unavailable for disconnected users)
  useEffect(() => {
    if (!isActive || !currentStep) return;
    if (!currentStep.skipIfTargetMissing && !currentStep.skipIfElementExists) return;

    const timeout = setTimeout(() => {
      if (currentStep.skipIfTargetMissing) {
        const target = document.querySelector(currentStep.targetSelector);
        if (!target) {
          nextStep();
          return;
        }
      }
      if (currentStep.skipIfElementExists) {
        const blocker = document.querySelector(currentStep.skipIfElementExists);
        if (blocker) {
          exitTutorial();
          return;
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [currentStep, isActive, nextStep, exitTutorial]);

  const handleRequestExit = useCallback(() => {
    setShowExitConfirm(true);
  }, []);

  const handleConfirmExit = useCallback(() => {
    setShowExitConfirm(false);
    exitTutorial();
  }, [exitTutorial]);

  const handleCancelExit = useCallback(() => {
    setShowExitConfirm(false);
  }, []);

  // Handle Escape key to show exit confirmation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleRequestExit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, handleRequestExit]);

  // Disable body scroll when tutorial is active
  useEffect(() => {
    document.body.style.overflow = isActive ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isActive]);

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          className="absolute inset-0"
          onClick={handleRequestExit}
          role="button"
          tabIndex={0}
          aria-label="Exit tutorial"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleRequestExit();
            }
          }}
        />

        <Spotlight targetSelector={currentStep.targetSelector} padding={8} />

        <TutorialCard
          step={currentStep}
          currentStepNumber={currentStepIndex + 1}
          totalSteps={getTotalSteps()}
          onRequestExit={handleRequestExit}
          narration={{
            available: true,
            enabled: narrationEnabled,
            isNarrating,
            onToggle: toggleNarration,
          }}
        />

        <ExitConfirmationModal
          isOpen={showExitConfirm}
          onConfirm={handleConfirmExit}
          onCancel={handleCancelExit}
        />
      </motion.div>
    </AnimatePresence>
  );
}
